/**
 * Sentry event filters.
 *
 * Some HTTP errors are part of the normal product UX (duplicate name → 409,
 * invalid form → 422, missing auth → 401, expired subscription → 402/403)
 * and are already shown to the user via toast notifications. They are not
 * bugs and must not be reported as Sentry errors.
 */

import { isChunkLoadError } from '../services/pwa/chunk-error-reloader';
import { isClerkNetworkError } from './clerk-error-handler';

const EXPECTED_CLIENT_STATUSES = new Set([400, 401, 402, 403, 404, 409, 422, 429]);

interface MaybeHttpError {
  name?: string;
  response?: { status?: number };
}

export function isExpectedClientHttpError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const candidate = error as MaybeHttpError;
  if (candidate.name !== 'HTTPError') {
    return false;
  }
  const status = candidate.response?.status;
  return typeof status === 'number' && EXPECTED_CLIENT_STATUSES.has(status);
}

/**
 * Chunk-load errors are already self-recovered by chunk-error-reloader.ts
 * (page reload after a deploy) — they must not also pollute Sentry.
 */
export function isDroppableClientError(error: unknown): boolean {
  return isExpectedClientHttpError(error) || isChunkLoadError(error) || isClerkNetworkError(error);
}
